/**
 * In-app notification centre (top-bar bell). Holds this session's
 * notifications: connector freshness alerts raised by the shell, live
 * connection problems and anything a module raises with `notify()`.
 * Text must never contain PHI (it can be shown on a shared screen and is
 * kept in memory only).
 *
 * Persistent, cross-device notifications need a server table; see
 * docs/requests/wp0-shell-notifications.md.
 */
export type NotificationTone = 'info' | 'success' | 'warning' | 'danger';

export interface NotificationInput {
  title: string;
  body?: string;
  tone?: NotificationTone;
  /** App path to open when clicked. */
  href?: string;
  /** Re-using a key replaces the earlier notification (e.g. one per office alert). */
  key?: string;
}

export interface ShellNotification {
  id: string;
  key: string | null;
  title: string;
  body: string | null;
  tone: NotificationTone;
  href: string | null;
  at: number;
  read: boolean;
}

export const MAX_NOTIFICATIONS = 50;

export function createNotificationStore(now: () => number = Date.now) {
  let items: ShellNotification[] = [];
  let seq = 0;
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const l of listeners) l();
  };
  return {
    notify(input: NotificationInput): string {
      seq += 1;
      const n: ShellNotification = {
        id: `n${seq}`,
        key: input.key ?? null,
        title: input.title,
        body: input.body ?? null,
        tone: input.tone ?? 'info',
        href: input.href ?? null,
        at: now(),
        read: false,
      };
      const rest = n.key === null ? items : items.filter((x) => x.key !== n.key);
      items = [n, ...rest].slice(0, MAX_NOTIFICATIONS);
      emit();
      return n.id;
    },
    /** Removes the notification with this key (the condition cleared). */
    resolve(key: string): void {
      const next = items.filter((x) => x.key !== key);
      if (next.length !== items.length) {
        items = next;
        emit();
      }
    },
    markAllRead(): void {
      if (items.every((x) => x.read)) return;
      items = items.map((x) => (x.read ? x : { ...x, read: true }));
      emit();
    },
    dismiss(id: string): void {
      items = items.filter((x) => x.id !== id);
      emit();
    },
    clear(): void {
      items = [];
      emit();
    },
    get: (): readonly ShellNotification[] => items,
    unread: (): number => items.filter((x) => !x.read).length,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type NotificationStore = ReturnType<typeof createNotificationStore>;

export const notifications = createNotificationStore();

/** Raises a notification in the top-bar bell. Returns its id. */
export function notify(input: NotificationInput): string {
  return notifications.notify(input);
}
